// src/services/products.js
import http from "../lib/http";

/**
 * Fetch products with full filtering support.
 *
 * Supported params (all optional):
 *   page          – page number (default 1)
 *   limit         – items per page (default 30)
 *   query         – search by product name
 *   category_name – filter by category name (e.g. "Womens wear")
 *   min_price     – minimum price filter
 *   max_price     – maximum price filter
 *
 * API response shape:
 *   {
 *     message: string,
 *     total_products: number,
 *     returned_count: number,
 *     pagination: { current_page, per_page, total, last_page, next_page_url, prev_page_url },
 *     seed: number,
 *     data: Product[]
 *   }
 *
 * Each Product has:
 *   id, name, slug, sku, price, final_price, product_discount_price,
 *   flash_sale_price, pricing_type, is_flash_sale, sold_count,
 *   average_rating, total_reviews, product_images: string[],
 *   category: { id, category_name, category_image }
 */
export const getProducts = ({
  page = 1,
  limit = 30,
  query = "",
  category_name = "",
  min_price = "",
  max_price = "",
} = {}) => {
  const params = { page, limit };

  if (query.trim())         params.query         = query.trim();
  if (category_name.trim()) params.category_name = category_name.trim();
  if (min_price !== "")     params.min_price     = min_price;
  if (max_price !== "")     params.max_price     = max_price;

  return http.get("/api/customer/products", { params });
};

/**
 * Convenience wrapper used by HeroSection / best-selling grids.
 * Returns the same shape as getProducts().
 */
export const getBestSellingProducts = ({ limit = 30, page = 1 } = {}) =>
  getProducts({ limit, page });

export const getProductById   = (id)   => http.get(`/api/customer/products/${id}`);
export const getProductBySlug = (slug) => http.get(`/api/customer/products/${slug}`);

/**
 * Related products by category name.
 *
 * Uses category.category_name from the product response (e.g. "Womens wear").
 * The component should read p.category.category_name and pass it here.
 */
export const getRelatedProducts = ({ categoryName, excludeId, limit = 16 }) =>
  getProducts({ category_name: categoryName, limit });

export const getProductReviews = (productId) =>
  http.get(`/api/customer/reviews/all/${productId}`);

export const getFlashSaleProducts = () =>
  http.get("/api/customer/flash-sales");